import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

import { AmorphicService } from '../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { Controller } from '../../Controller';
import { CoverpathUtils } from '../../../../../coverpath-common/js/static/CoverpathUtils';
import { QuickQuoteService } from '../../services/quote/quick-quote.service';
import { QuoteCardService } from './quote-card/quote-card.service';
import { SafeInviteInfo } from '../../../../../common/js/customer/SafeInviteInfo';
import { ToastService } from '../../../../../coverpath-common/angular/components/overlays/toaster/toast-service.service';
import { SubscribingComponent } from '../../../../../coverpath-common/components/subscribing-component/subscribing.component';
import { CONVERSION_INVITE_STRING } from '../../../../../common/js/constants/controller';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';
import {
  ToasterComponent,
  ToasterIntent,
} from '../../../../../coverpath-common/angular/components/overlays/toaster/toaster.component';

const NB_DECOMMISSION_BANNER_TEXT =
  'Coverpath is no longer accepting new applications. Please speak with your financial professional to submit a new application.';

declare let window;
@Component({
  moduleId: module.id,
  selector: 'home-component',
  templateUrl: './home.component.html',
})
export class HomeComponent extends SubscribingComponent implements OnInit {
  @ViewChild('toasterInstance') toasterInstance: ToasterComponent;

  private token: string;

  controller: Controller;

  private email: string;
  clientName: string;
  agentName: string;
  hasAgentQuote: boolean = false;
  isPersonalized: boolean = false;
  isAgentAssisted: boolean = false;
  termQuoteNotART: boolean = false;
  isConversionInvite: boolean = false;
  quoteType: string = '';
  appSubmitted: boolean = false;
  loggedIn: boolean = false;
  advisorDescriptiveNoun: string;
  private safeInviteInfo: SafeInviteInfo;
  nbDecommissionPost30Days: boolean = false;

  constructor(
    private amorphicService: AmorphicService,
    private route: ActivatedRoute,
    private router: Router,
    private quickQuoteService: QuickQuoteService,
    private quoteCardService: QuoteCardService,
    private toastService: ToastService
  ) {
    super();
    this.controller = amorphicService.controller as Controller;
    this.isConversionInvite = window.location.href.includes(CONVERSION_INVITE_STRING);
    this.nbDecommissionPost30Days = this.controller.nbDecommissionPost30Days;
  }

  async ngOnInit() {
    this.advisorDescriptiveNoun = CoverpathUtils.getAdvisorDescriptiveNoun();
    this.quickQuoteService.triggerQuickQuoteLoad.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!!this.safeInviteInfo) {
        this.updateProps();
      }
    });

    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe((fragment) => {
      if (fragment === 'verify_email_change') {
      } else if (fragment === 'reset_password_from_code') {
        this.router.navigate(['login'], { queryParams: { reset_password: true }, fragment: 'Reset' });
      }
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((param) => {
      if (param.token) {
        this.token = param.token;
        this.email = param.email;
        this.setPasswordChangeHash();
      } else if (param.code) {
        this.controller.verifyEmailCode = param.code;
        this.controller.email = param.email;
        this.router.navigate(['login'], { fragment: 'NewEmail' });
      }
    });

    if (typeof window !== 'undefined') {
      const onHomePage = window.location.pathname === '/' || window.location.pathname === '/home';

      this.loggedIn = this.controller.loggedIn;

      if (onHomePage && !this.loggedIn) {
        const validAgentCode = /\?fg=([\w-]*)[^\&]*/;
        const genericLinkMatch = window.location.search.match(validAgentCode);
        if (genericLinkMatch) {
          const agentReferralCode = genericLinkMatch[1];
          await this.controller.inviteController.processAgentReferralCode(agentReferralCode);
          this.controller.customer.setPaidSource();
          this.hasAgentQuote = false;
        }
        const inviteCode = CoverpathUtils.extractInvitationCode(window.location.href);
        if (inviteCode) {
          this.hasAgentQuote = true;
          UILoggerSingleton.getInstance().info(
            {
              module: 'Controller',
              activity: 'clientInit',
              category: UICategory.request,
            },
            `Passing to invite controller coverpath-agent invite code of ${inviteCode}`
          );

          await this.controller.inviteController.processInviteCode(inviteCode, false);
          this.safeInviteInfo = await this.controller.inviteController.getSafeInviteInfo();
          this.quickQuoteService.loadQuickQuote();
        }
      } else if (onHomePage && this.loggedIn) {
        this.router.navigate(['account-center']);
      }
    }
    this.updateProps();
    this.quoteCardService.loadProductData();
  }

  async ngAfterViewInit() {
    if (this.nbDecommissionPost30Days) {
      this.toasterInstance.notify(NB_DECOMMISSION_BANNER_TEXT, { intent: ToasterIntent.DECOMMISSION });
    }
  }

  private updateProps(): void {
    if (!!this.safeInviteInfo === false) {
      return;
    }
    this.clientName = this.safeInviteInfo.firstName;
    const advisorQuote = this.safeInviteInfo.advisorQuote;

    if (advisorQuote && advisorQuote.hasBeenInitialized) {
      this.hasAgentQuote = true;
      this.quoteType = advisorQuote.isJoint ? 'joint' : advisorQuote.productType.toLowerCase();
      this.termQuoteNotART = this.quoteType === 'term' && advisorQuote.term > 1;
    }

    if (this.safeInviteInfo.isPersonalized) {
      this.isPersonalized = true;
      this.agentName = this.safeInviteInfo.safePolicyInfo.primaryAgent.displayName;
      this.appSubmitted = this.safeInviteInfo.safePolicyInfo.submitted;
    }
    CoverpathUtils.scrollToTop();
  }

  isFinishedLoading(): boolean {
    return !!this.quoteType && this.quoteCardService.productDataLoaded;
  }

  hasAgent(): boolean {
    if (!!this.safeInviteInfo === false) {
      const app = this.controller.customer.application;
      return app && app.primaryPolicy && app.primaryPolicy.primaryPolicyAgent
        ? !!app.primaryPolicy.primaryPolicyAgent.primaryAgent
        : false;
    } else {
      return this.safeInviteInfo.hasPrimaryAgent;
    }
  }

  isGeneric(): boolean {
    return this.hasAgent() && !this.hasAgentQuote;
  }

  scrollToQuickQuote(): void {
    CoverpathUtils.scrollToElement('quick-quote');
  }

  doesQuickQuoteExist(): boolean {
    return CoverpathUtils.doesQuickQuoteExist();
  }

  setPasswordChangeHash(): void {
    this.controller.passwordChangeHash = this.token;
    this.controller.email = this.email;
    this.controller.publicSave();
  }

  getImageClassLeft() {
    return {
      'hero__image--left-annuity': this.quoteType === 'annuity',
      'hero__image--left-life': this.quoteType !== 'annuity',
    };
  }

  getImageClassRight() {
    return {
      'hero__image--right-annuity': this.quoteType === 'annuity',
      'hero__image--right-life': this.quoteType !== 'annuity',
    };
  }
}
